<script setup lang="ts">
// The district map: the service-area boundary, then the wind field (the lines
// and their downstream arrowheads), then one marker per positioned technician —
// the ones whose latest Nidus Notes breadcrumb placed them somewhere. A
// technician with no breadcrumb has no place to draw and is left off the map,
// though the roster still lists them. A positioned technician with a breadcrumb
// today is drawn in green; one without is grey.
//
// The field is drawn as streamlines, coloured by speed on a fixed 0–20 km/h
// plasma ramp, each ended downstream by a filled arrowhead — the arrowheads
// are how direction is read.
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import {
	MglFillLayer,
	MglGeoJsonSource,
	MglLineLayer,
	MglMap,
	MglMarker,
	useMap,
} from "vue-maplibre-gl";
import { boundsFromGeoJSON, boundsMarkers, mapStyleURL } from "@/map/util";
import { ALIDADE_SMOOTH } from "@/map/constants";
import type {
	HomeBoundary,
	HomeTechnician,
	HomeWindArrow,
	HomeWindLine,
	Location,
} from "@/type/api";
import type { Marker } from "@/types";
import type {
	ExpressionSpecification,
	FillLayerSpecification,
	LngLatBoundsLike,
	LineLayerSpecification,
} from "maplibre-gl";
import type { GeoJSON } from "geojson";

const props = defineProps<{
	technicians: HomeTechnician[];
	windLines: HomeWindLine[];
	windArrows: HomeWindArrow[];
	boundary: HomeBoundary | null;
}>();

/**
 * District service area (WGS84), lon −119.573..−118.858, lat 36.194..36.661.
 * It is the last-resort frame: the map fits the district boundary when the
 * payload carries one, the technician positions when it does not, and only
 * falls back here when there is nothing at all to fit.
 */
const DISTRICT_BOUNDS: [[number, number], [number, number]] = [
	[-119.573, 36.194],
	[-118.858, 36.661],
];

/**
 * The technicians that have a position. The roster carries everyone, but a
 * technician with no breadcrumb has no place to draw, so the map — and the
 * frame it fits — sees only these.
 */
const positioned = computed(() =>
	props.technicians.filter(
		(t): t is HomeTechnician & { location: Location } => t.location !== null,
	),
);

/**
 * The frame the map fits: the service-area boundary first, then the technician
 * positions, then the district constant. One computed feeds both the initial
 * `:bounds` and the Recenter button, so the two can never disagree. It is
 * handed to the map as its `bounds` prop, so maplibre re-fits by itself when
 * the boundary or the technicians arrive after the map is built.
 */
const mapBounds = computed<LngLatBoundsLike>(() => {
	if (props.boundary) {
		const fromBoundary = boundsFromGeoJSON(props.boundary.geojson);
		if (fromBoundary) return fromBoundary;
	}
	if (positioned.value.length > 0) {
		const markers: Marker[] = positioned.value.map((t) => ({
			id: `${t.user.id}`,
			location: t.location,
		}));
		return boundsMarkers(markers);
	}
	return DISTRICT_BOUNDS;
});

/**
 * The district boundary as a single Feature, or null when the organisation has
 * no service area — the source is then omitted rather than drawn empty.
 */
const boundaryGeoJSON = computed<GeoJSON.Feature | null>(() =>
	props.boundary
		? {
				type: "Feature",
				geometry: props.boundary.geojson,
				properties: {},
			}
		: null,
);

const activeCount = computed(
	() => props.technicians.filter((t) => t.active_today).length,
);

/**
 * The wind ramp is fixed: 0–20 km/h, plasma, nine evenly spaced stops. It is a
 * shared scale, not a per-view one — a district's wind is read the same way on
 * every screen that draws it.
 */
const WIND_RAMP = [
	"#0d0887",
	"#4c02a1",
	"#7e03a8",
	"#aa2395",
	"#cc4778",
	"#e66c5c",
	"#f89540",
	"#fdc527",
	"#f0f921",
];
const WIND_RAMP_MAX_KPH = 20;

const windRampCss = `linear-gradient(90deg, ${WIND_RAMP.join(", ")})`;

// The ramp as a maplibre `interpolate` expression. The spec's expression union
// cannot be assembled from a spread colour list, so the built expression is
// asserted to ExpressionSpecification — a maplibre type, not a blanket `any`.
const windColorExpression = [
	"interpolate",
	["linear"],
	["get", "speed_kph"],
	...WIND_RAMP.flatMap((color, i) => [
		(i * WIND_RAMP_MAX_KPH) / (WIND_RAMP.length - 1),
		color,
	]),
] as unknown as ExpressionSpecification;

const windLinePaint = {
	"line-color": windColorExpression,
	"line-width": 1.6,
	"line-opacity": 0.25,
} satisfies LineLayerSpecification["paint"];

// Arrowheads carry the same speed colour as the streamline they sit on, so a
// head reads as part of its line rather than a separate dark marker. Quarter
// opacity keeps them legible without letting them dominate the map.
const windArrowPaint = {
	"fill-color": windColorExpression,
	"fill-opacity": 0.25,
} satisfies FillLayerSpecification["paint"];

const windLinesGeoJson = computed<GeoJSON.FeatureCollection>(() => ({
	type: "FeatureCollection",
	features: props.windLines.map((line) => ({
		type: "Feature" as const,
		geometry: { type: "LineString" as const, coordinates: line.coordinates },
		properties: { kind: "line", speed_kph: line.speed_kph },
	})),
}));

/**
 * One filled triangle per arrowhead, wrapping the unclosed `ring` as an
 * explicitly closed polygon ring so the fill layer draws the whole triangle.
 */
const windArrowsGeoJson = computed<GeoJSON.FeatureCollection>(() => ({
	type: "FeatureCollection",
	features: props.windArrows.map((arrow) => ({
		type: "Feature" as const,
		geometry: {
			type: "Polygon" as const,
			coordinates: [[...arrow.ring, arrow.ring[0]]],
		},
		properties: { speed_kph: arrow.speed_kph },
	})),
}));

const mapInstance = useMap();
const mapContainer = ref<HTMLElement | null>(null);

function fitDistrict() {
	const map = mapInstance.map;
	if (!map) return;
	map.fitBounds(mapBounds.value, { padding: 24, animate: false });
}

/**
 * The map is constructed before the flex layout has given the card its final
 * height, so the initial fit runs against the wrong box — the district would be
 * framed for a container a tenth of its size. The observer refits whenever the
 * container settles (and on later window resizes).
 */
let containerObserver: ResizeObserver | null = null;

onMounted(() => {
	const container = mapContainer.value;
	if (!container) return;
	containerObserver = new ResizeObserver(fitDistrict);
	containerObserver.observe(container);
});

onBeforeUnmount(() => containerObserver?.disconnect());
</script>

<template>
	<div class="card home-map-card">
		<div class="card-header d-flex align-items-center gap-2 py-1">
			<span class="fw-semibold">District — technicians and wind</span>
			<span class="badge rounded-pill text-bg-success">
				{{ activeCount }} active today
			</span>
			<button
				type="button"
				class="btn btn-sm btn-outline-secondary ms-auto py-0"
				@click="fitDistrict"
			>
				<i class="bi bi-crosshair me-1" aria-hidden="true"></i>Recenter
			</button>
		</div>
		<div ref="mapContainer" class="card-body p-0 map-body">
			<mgl-map
				:map-style="mapStyleURL(ALIDADE_SMOOTH)"
				:bounds="mapBounds"
				:fit-bounds-options="{ padding: 24 }"
				height="100%"
			>
				<!-- The district boundary sits under everything else: the wind
				     field and the markers are read against it, not through it. -->
				<mgl-geo-json-source
					v-if="boundaryGeoJSON"
					source-id="home-district-boundary"
					:data="boundaryGeoJSON"
				>
					<mgl-line-layer
						layer-id="home-district-boundary"
						:paint="{
							'line-color': '#0d6efd',
							'line-width': 2.5,
							'line-opacity': 0.8,
						}"
					/>
				</mgl-geo-json-source>

				<!-- The streamlines first, then their arrowheads, so a head is
				     read on top of its own line. -->
				<mgl-geo-json-source
					source-id="home-wind-lines"
					:data="windLinesGeoJson"
				>
					<mgl-line-layer layer-id="home-wind-lines" :paint="windLinePaint" />
				</mgl-geo-json-source>

				<mgl-geo-json-source
					source-id="home-wind-arrows"
					:data="windArrowsGeoJson"
				>
					<mgl-fill-layer layer-id="home-wind-arrows" :paint="windArrowPaint" />
				</mgl-geo-json-source>

				<mgl-marker
					v-for="t in positioned"
					:key="t.user.id"
					:coordinates="[t.location.longitude, t.location.latitude]"
					anchor="bottom"
				>
					<template #marker>
						<span
							class="tech-pin"
							:class="t.active_today ? 'tech-pin--active' : 'tech-pin--idle'"
							:title="`${t.user.display_name} — ${t.active_today ? 'breadcrumb today' : 'last seen earlier'}`"
						>
							<img
								v-if="t.user.avatar"
								:src="t.user.avatar"
								:alt="t.user.display_name"
								class="tech-pin__photo"
							/>
							<template v-else>{{ t.user.initials }}</template>
						</span>
					</template>
				</mgl-marker>
			</mgl-map>
		</div>
		<div
			class="card-footer d-flex flex-wrap align-items-center gap-3 small text-muted py-1"
		>
			<span class="d-inline-flex align-items-center gap-1">
				<span class="legend-dot legend-dot--active"></span>
				breadcrumb today
			</span>
			<span class="d-inline-flex align-items-center gap-1">
				<span class="legend-dot legend-dot--idle"></span>
				last seen earlier
			</span>
			<span class="d-inline-flex align-items-center gap-2">
				wind
				<span class="wind-ramp" :style="{ background: windRampCss }"></span>
				<span>0–20 km/h · arrow = direction</span>
			</span>
		</div>
	</div>
</template>

<style scoped>
.home-map-card {
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	min-height: 0;
}

/* The map takes whatever height the card has left, so the page fills the
 * screen rather than leaving a gap under a fixed-height map. */
.map-body {
	flex: 1 1 auto;
	min-height: 0;
}

.tech-pin {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	border: 2px solid #fff;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
	color: #fff;
	font-size: 0.65rem;
	font-weight: 700;
	line-height: 1;
	cursor: default;
}

.tech-pin--active {
	background: #198754;
}

.tech-pin--idle {
	background: #adb5bd;
}

/* The face fills the pin, sized so the state colour still shows as a ring
 * inside the white border — the marker keeps its 28px footprint and still
 * says at a glance whether the technician is out today. */
.tech-pin__photo {
	display: block;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	object-fit: cover;
}

.legend-dot {
	width: 12px;
	height: 12px;
	border-radius: 50%;
	border: 2px solid #fff;
	box-shadow: 0 0 0 1px #ced4da;
	display: inline-block;
}

.legend-dot--active {
	background: #198754;
}

.legend-dot--idle {
	background: #adb5bd;
}

.wind-ramp {
	width: 120px;
	height: 10px;
	border-radius: 3px;
	border: 1px solid #ced4da;
	display: inline-block;
}
</style>
